import React from 'react';
import { Link } from 'react-router-dom';
import { Droplets, Zap, Gauge, Truck, HardHat, ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';

const products = [
  {
    icon: Droplets,
    name: 'Pumps',
    blurb:
      'Industrial pumping equipment across duties, fluid types and site conditions — from water and slurry to process and dewatering applications.',
  },
  {
    icon: Zap,
    name: 'Electric Motors',
    blurb:
      'Motors and drives specified to load, environment and efficiency requirements for mining, manufacturing and utilities.',
  },
  {
    icon: Gauge,
    name: 'Valves',
    blurb:
      'Isolation, control and safety valves for process, water and mining applications — engineered for reliability under pressure.',
  },
  {
    icon: Truck,
    name: 'Yellow Plant',
    blurb:
      'Earthmoving and heavy construction equipment for mining, civils and infrastructure projects across South Africa.',
  },
  {
    icon: HardHat,
    name: 'DTH & Top Hammer Mining Bits',
    blurb:
      'Down-the-hole and top hammer drilling consumables engineered for mining and quarrying performance and longevity.',
  },
];

const IndustrialOverview = () => {
  const scrollToRange = () => {
    document.getElementById('range')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Hero */}
      <section
        id="home"
        className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#252525] to-[#2d2d2d]"
      >
        <div className="absolute inset-0 overflow-hidden opacity-25 pointer-events-none">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="ov-grid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="rgba(62,181,160,0.18)" strokeWidth="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ov-grid)" />
          </svg>

          <svg
            viewBox="0 0 600 600"
            className="absolute right-[-6%] top-1/2 -translate-y-1/2 w-[520px] h-[520px] hidden lg:block opacity-70"
            aria-hidden="true"
          >
            <circle cx="350" cy="300" r="220" fill="none" stroke="rgba(62,181,160,0.35)" strokeWidth="1.5" />
            <circle cx="350" cy="300" r="160" fill="none" stroke="rgba(62,181,160,0.3)" strokeWidth="1.2" />
            <circle cx="350" cy="300" r="100" fill="none" stroke="rgba(62,181,160,0.28)" strokeWidth="1" />
            <circle cx="350" cy="300" r="6" fill="#3eb5a0" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 pt-32 md:pt-40 w-full">
          <div className="max-w-5xl">
            <p
              className="animate-hero-rise inline-block text-xs md:text-sm uppercase tracking-[0.3em] text-[#3eb5a0] font-medium mb-8 md:mb-12 border border-[#3eb5a0]/50 rounded-full px-4 py-1.5"
              style={{ animationDelay: '0ms' }}
            >
              Reshadi Industrial
            </p>

            <h1
              className="animate-hero-rise text-3xl md:text-5xl lg:text-7xl font-bold text-white leading-[1.15] tracking-tight mb-8 md:mb-10 max-w-5xl"
              style={{ animationDelay: '120ms' }}
            >
              Industrial B2B
              <br />
              <span className="text-[#3eb5a0]">Solutions.</span>
            </h1>

            <p
              className="animate-hero-rise text-lg md:text-xl lg:text-2xl text-white/80 font-light tracking-wide leading-snug max-w-3xl mb-8 md:mb-10"
              style={{ animationDelay: '240ms' }}
            >
              A focused range of industrial products and services for South African mining,
              civils and process operations — supplied directly, and paired with intelligent
              B2B matchmaking through our partnership with Headcore.
            </p>

            <div
              className="animate-hero-rise flex flex-col sm:flex-row gap-4"
              style={{ animationDelay: '360ms' }}
            >
              <button
                onClick={scrollToRange}
                data-testid="overview-explore-btn"
                className="group inline-flex items-center justify-center gap-3 px-7 md:px-9 py-3.5 md:py-4 bg-[#3eb5a0] text-black font-semibold rounded-full shadow-lg hover:bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#3eb5a0] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                <span>Explore our range</span>
                <ArrowRight
                  size={18}
                  strokeWidth={2.4}
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                />
              </button>

              <Link
                to="/industrial/headcore"
                data-testid="overview-headcore-link"
                className="group inline-flex items-center justify-center gap-3 px-7 md:px-9 py-3.5 md:py-4 bg-transparent border-2 border-[#f5b400] text-[#f5b400] font-semibold rounded-full hover:bg-[#f5b400] hover:text-[#1b2a55] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#f5b400] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                <span>Headcore matchmaking</span>
                <ArrowUpRight
                  size={18}
                  strokeWidth={2.4}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
            </div>
          </div>
        </div>

        <button
          onClick={scrollToRange}
          aria-label="Scroll to product range"
          className="absolute bottom-6 md:bottom-8 left-6 md:left-12 w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-[#3eb5a0] bg-transparent text-[#3eb5a0] flex items-center justify-center hover:bg-[#3eb5a0] hover:text-black transition-all duration-300"
        >
          <ChevronDown size={22} />
        </button>
      </section>

      {/* Product range */}
      <section
        id="range"
        className="relative py-16 md:py-24 lg:py-28 bg-[#1a1a1a]"
        data-testid="overview-range"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-10 md:mb-14 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#3eb5a0] font-semibold mb-3">
              Our B2B Offering
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Five product lines.
              <br />
              <span className="text-[#3eb5a0]">One point of accountability.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-12 md:mb-14">
            {products.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.name}
                  data-testid={`product-card-${i}`}
                  className="group relative overflow-hidden rounded-2xl bg-[#252525] border border-[#3eb5a0]/15 p-6 md:p-8 transition-all duration-300 hover:border-[#3eb5a0] hover:-translate-y-1 hover:shadow-2xl"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#3eb5a0]/10 border border-[#3eb5a0]/30 flex items-center justify-center mb-5 md:mb-6 transition-colors duration-300 group-hover:bg-[#3eb5a0]">
                    <Icon
                      size={22}
                      className="text-[#3eb5a0] transition-colors duration-300 group-hover:text-black"
                      strokeWidth={2}
                    />
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold text-white mb-3">{p.name}</h3>
                  <p
                    className="text-sm md:text-base text-white/65 font-light"
                    style={{ lineHeight: '1.75' }}
                  >
                    {p.blurb}
                  </p>
                </article>
              );
            })}
          </div>

          {/* Next-step CTAs */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-5 max-w-4xl">
            <Link
              to="/industrial/pumps"
              data-testid="overview-cta-pumps"
              className="group rounded-2xl bg-[#3eb5a0]/[0.06] border border-[#3eb5a0]/25 p-6 md:p-7 hover:border-[#3eb5a0] hover:bg-[#3eb5a0]/[0.1] transition-all duration-300 flex items-center justify-between gap-4"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#3eb5a0] font-semibold mb-2">Deep-dive</p>
                <p className="text-base md:text-lg text-white font-semibold">Industrial Pumps</p>
                <p className="text-sm text-white/60 font-light">Turnkey pumping supply, installation and support.</p>
              </div>
              <ArrowUpRight size={22} className="text-[#3eb5a0] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 flex-shrink-0" />
            </Link>

            <Link
              to="/industrial/headcore"
              data-testid="overview-cta-headcore"
              className="group rounded-2xl bg-[#f5b400]/[0.06] border border-[#f5b400]/25 p-6 md:p-7 hover:border-[#f5b400] hover:bg-[#f5b400]/[0.1] transition-all duration-300 flex items-center justify-between gap-4"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#f5b400] font-semibold mb-2">Featured Platform</p>
                <p className="text-base md:text-lg text-white font-semibold">Headcore B2B Matchmaking</p>
                <p className="text-sm text-white/60 font-light">AI matching that connects buyers and suppliers of technical work.</p>
              </div>
              <ArrowUpRight size={22} className="text-[#f5b400] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 flex-shrink-0" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default IndustrialOverview;
